import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/config';
import { Table, Button, Modal, Typography, Card, Row, Col, Input, Tag, Space, Avatar, Tooltip } from 'antd';
import { 
  TeamOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  CloseCircleOutlined, 
  SearchOutlined, 
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  UndoOutlined
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import moment from 'moment';
import AssociationRegister from '../../components/auth/AssociationRegister';
import RejectReasonModal from '../../components/admin/RejectReasonModal';

const { Title, Text } = Typography;

const AssociationManagement = () => {
  const [associations, setAssociations] = useState([]);
  const [filteredAssociations, setFilteredAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAssociation, setEditingAssociation] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch associations from API
  const loadAssociations = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/associations');
      if (response.data && response.data.success) {
        setAssociations(response.data.associations);
        setFilteredAssociations(response.data.associations);
      } else {
        throw new Error(response.data?.message || 'Failed to fetch associations');
      }
    } catch (error) {
      console.error('Error loading associations:', error);
      toast.error('Error loading associations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssociations();
  }, []);

  // Search Logic
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    const filtered = associations.filter((association) => 
      association.associationName?.toLowerCase().includes(value) ||
      association.email?.toLowerCase().includes(value) ||
      association.phone?.includes(value) ||
      association.city?.toLowerCase().includes(value)
    );
    setFilteredAssociations(filtered);
  };

  const handleReset = () => {
    setSearchText('');
    setFilteredAssociations(associations);
  };

  // Toggle verification status
  const handleToggleVerification = async (associationId, currentStatus) => {
    Modal.confirm({
      title: `${currentStatus ? 'Unverify' : 'Verify'} Association`,
      content: `Are you sure you want to ${currentStatus ? 'unverify' : 'verify'} this association?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          const response = await api.put(`/admin/associations/${associationId}/verify`, {
            isVerified: !currentStatus
          });
          
          if (response.data && response.data.success) {
            toast.success(`Association ${!currentStatus ? 'verified' : 'unverified'} successfully`);
            loadAssociations();
          }
        } catch (error) {
          console.error('Error toggling verification:', error);
          toast.error('Failed to update verification status');
        }
      }
    });
  };

  // Approve / Reject registration status
  const handleApprove = (associationId) => {
    Modal.confirm({
      title: 'Approve Association Registration',
      content: 'Are you sure you want to approve this association?',
      okText: 'Approve',
      okType: 'primary',
      onOk: async () => {
        setActionLoading(true);
        try {
          const response = await api.put(`/admin/associations/${associationId}/approve`);
          if (response.data && response.data.success) {
            toast.success('Association approved successfully');
            loadAssociations();
          }
        } catch (error) {
          toast.error('Failed to approve association');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleRevert = (associationId) => {
    Modal.confirm({
      title: 'Revert to Pending',
      content: 'This will move the record back to Pending review and it will be hidden from the public site if currently approved. Continue?',
      okText: 'Revert',
      onOk: async () => {
        setActionLoading(true);
        try {
          const response = await api.put(`/admin/associations/${associationId}/revert`);
          if (response.data && response.data.success) {
            toast.success('Association reverted to pending');
            loadAssociations();
          }
        } catch (error) {
          toast.error('Failed to revert association');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleReject = (association) => {
    setRejectTarget(association);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const submitRejection = async () => {
    if (!rejectReason.trim()) {
      toast.warning('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    try {
      const response = await api.put(`/admin/associations/${rejectTarget._id}/reject`, {
        reason: rejectReason
      });
      if (response.data && response.data.success) {
        toast.success('Association rejected');
        setRejectModalOpen(false);
        loadAssociations();
      }
    } catch (error) {
      toast.error('Failed to reject association');
    } finally {
      setActionLoading(false);
    }
  };

  // Modal handlers
  const openAddModal = () => {
    setEditingAssociation(null);
    setIsModalOpen(true);
  };

  const handleEdit = (association) => {
    setEditingAssociation(association);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setTimeout(() => setEditingAssociation(null), 300); // Delay clearing to allow modal animation
  };

  const handleEditSuccess = () => {
    handleModalClose();
    loadAssociations();
  };

  // Delete Association
  const handleDelete = (associationId) => {
    Modal.confirm({
      title: 'Delete Association',
      content: 'Are you sure you want to delete this association? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await api.delete(`/admin/associations/${associationId}`);
          
          if (response.data && response.data.success) {
            toast.success('Association deleted successfully');
            loadAssociations();
          } else {
            throw new Error(response.data?.message || 'Failed to delete association');
          }
        } catch (error) {
          toast.error('Failed to delete association.');
        }
      }
    });
  };

  // Statistics Calculation
  const stats = {
    total: associations.length,
    verified: associations.filter(a => a.isVerified).length,
    active: associations.filter(a => a.isActive).length,
    inactive: associations.filter(a => !a.isActive).length
  };

  // Styles based on the screenshot
  const cardStyles = {
    total: { bg: '#e6f7ff', color: '#1890ff', icon: <TeamOutlined /> },
    verified: { bg: '#f6ffed', color: '#52c41a', icon: <CheckCircleOutlined /> },
    active: { bg: '#fff7e6', color: '#fa8c16', icon: <CheckCircleOutlined /> },
    inactive: { bg: '#fff1f0', color: '#ff4d4f', icon: <CloseCircleOutlined /> }
  };

  // Table columns
  const columns = [
    {
      title: <span style={{ fontWeight: 500 }}>S.No</span>,
      key: 'index',
      width: 80,
      render: (text, record, index) => <Text style={{ color: '#666', fontSize:"16px" }}>{index + 1}</Text>,
    },
    {
      title: <span style={{ fontWeight: 500 }}>Association Name</span>,
      dataIndex: 'associationName',
      key: 'associationName',
      render: (text, record) => {
        // Construct proper image URL
        let imageUrl = '';
        if (record.logo) {
          if (record.logo.startsWith('http')) {
            imageUrl = record.logo;
          } else if (record.logo.startsWith('/')) {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${record.logo}`;
          } else {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/${record.logo}`;
          }
        }
        
        return (
          <Space>
            {record.logo ? (
              <Avatar src={imageUrl} size={40} />
            ) : (
              <Avatar style={{ backgroundColor: '#3C007A' }} size={40}>
                {text?.charAt(0)?.toUpperCase()}
              </Avatar>
            )}
            <div>
              <Text strong style={{ color: '#333', fontSize:"15px", display: 'block' }}>{text}</Text>
              <Text type="secondary" style={{ fontSize:"12px" }}>{record.registrationNumber || 'N/A'}</Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: <span style={{ fontWeight: 500 }}>Contact Info</span>,
      key: 'contact',
      render: (record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize:"14px" }}>
            <MailOutlined style={{ marginRight: 5, color: '#1890ff' }} />
            {record.email}
          </Text>
          <Text style={{ fontSize:"14px" }}>
            <PhoneOutlined style={{ marginRight: 5, color: '#52c41a' }} />
            {record.phone}
          </Text>
        </Space>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Location</span>,
      key: 'location',
      render: (record) => (
        <Text style={{ fontSize:"15px" }}>
          {record.district || (record.city && record.state ? `${record.city}, ${record.state}` : record.city || record.state) || 'N/A'}
        </Text>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>President</span>,
      dataIndex: 'presidentName',
      key: 'presidentName',
      render: (text) => <Text style={{ fontSize: "15px" }}>{text || 'N/A'}</Text>,
    },
    {
      title: <span style={{ fontWeight: 500 }}>Status</span>,
      key: 'status',
      render: (record) => {
        const statusColors = { pending: 'gold', approved: 'green', rejected: 'red' };
        return (
          <Space direction="vertical" size={4}>
            <Tag color={statusColors[record.status] || 'default'} style={{ fontSize:"14px" }}>
              {(record.status || 'pending').toUpperCase()}
            </Tag>
            <Tag
              color={record.isActive ? 'blue' : 'red'}
              style={{ fontSize:"14px" }}
            >
              {record.isActive ? 'Active' : 'Inactive'}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: <span style={{ fontWeight: 500 }}>Action</span>,
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.status === 'pending' && (
            <>
              <Tooltip title="Approve">
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  onClick={() => handleApprove(record._id)}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  size="small"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleReject(record)}
                />
              </Tooltip>
            </>
          )}
          {(record.status === 'approved' || record.status === 'rejected') && (
            <Tooltip title="Revert to Pending">
              <Button
                size="small"
                icon={<UndoOutlined />}
                onClick={() => handleRevert(record._id)}
              />
            </Tooltip>
          )}
          <Tooltip title={record.isVerified ? "Unverify" : "Verify"}>
            <Button
              size="small"
              type={record.isVerified ? "default" : "primary"}
              icon={<SafetyCertificateOutlined />}
              onClick={() => handleToggleVerification(record._id, record.isVerified)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              size="small"
              type="default"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record._id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0px', fontFamily: "'Rubik', sans-serif" }}>
      <Title level={3} style={{ marginBottom: '24px', fontFamily: "'Rubik', sans-serif", fontWeight: 400, color: '#3C007A' }}>
        Association Management
      </Title>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {Object.keys(stats).map((key) => (
          <Col xs={24} sm={12} md={6} key={key}>
            <Card 
              style={{ 
                background: cardStyles[key].bg, 
                border: 'none',
                borderRadius: '8px'
              }}
            >
              <Space direction="vertical" size={0}>
                <Text 
                  style={{ 
                    color: cardStyles[key].color, 
                    fontSize: '14px',
                    textTransform: 'capitalize',
                    fontFamily: "'Rubik', sans-serif"
                  }}
                >
                  {key === 'total' ? 'Total Associations' : 
                   key === 'verified' ? 'Verified' :
                   key === 'active' ? 'Active' : 'Inactive'}
                </Text>
                <Space>
                  <span style={{ color: cardStyles[key].color, fontSize: '24px' }}>
                    {cardStyles[key].icon}
                  </span>
                  <Title 
                    level={2} 
                    style={{ 
                      margin: 0, 
                      color: cardStyles[key].color,
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 500
                    }}
                  >
                    {stats[key]}
                  </Title>
                </Space>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Search and Actions */}
      <div style={{ 
        background: '#fff', 
        padding: '20px', 
        borderRadius: '8px 8px 0 0', 
        marginBottom: '0px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', gap: '8px', flex: 1, minWidth: '200px' }}>
          <Input 
            placeholder="Search associations by name, email, phone, or city"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={handleSearch}
            style={{ maxWidth: '400px', fontFamily: "'Rubik', sans-serif" }}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            Reset
          </Button>
        </div>
        <div className="flex items-center gap-4">
          <Text type="secondary" className="hidden md:block">
            Showing {filteredAssociations.length} of {associations.length} records
          </Text>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={openAddModal}
            style={{ backgroundColor: '#1890ff', borderRadius: '4px' }}
          >
            Add Association
          </Button>
        </div>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={filteredAssociations}
        loading={loading}
        rowKey={(record) => record._id}
        scroll={{ x: 'max-content' }}
        pagination={{
          pageSize: 10, 
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items` 
        }}
        rowClassName="align-middle"
        style={{ background: '#fff', borderRadius: '0 0 8px 8px' }}
      />

      {/* Edit Modal */}
      <Modal
        title={editingAssociation ? 'Edit Association' : 'Add New Association'}
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={1000}
        centered
        destroyOnClose
      >
        <AssociationRegister
          editingAssociation={editingAssociation}
          isAdminEdit={true}
          onSuccess={handleEditSuccess}
        />
      </Modal>

      <RejectReasonModal
        open={rejectModalOpen}
        title="Reject Association Registration"
        entityLabel={rejectTarget?.associationName}
        reason={rejectReason}
        onReasonChange={setRejectReason}
        onOk={submitRejection}
        onCancel={() => setRejectModalOpen(false)}
        confirmLoading={actionLoading}
      />
    </div>
  );
};

export default AssociationManagement;
